import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import API from "../api";
import { FeatureCollection, Geometry } from "geojson";
import { InferenceProperties } from "../types/inferences";
import { GetSceneInferencesFilters } from "../api/inferences";


function useSceneInferences(sceneId?: string, filters?: GetSceneInferencesFilters) {
  const [ isValidating, setIsValidating ] = useState<boolean>(false);
  const [ validationError, setValidationError ] = useState<string>();
  const [ isUpdating, setIsUpdating ] = useState<boolean>(false);
  const [ updateError, setUpdateError ] = useState<string>();

  const queryClient = useQueryClient();

  const validate = (options: {
    validationId: number;
    isValid: boolean;
    compositeIds?: number[];
  }) => {
    const { validationId, isValid, compositeIds } = options;

    if (!sceneId) return;

    setIsValidating(true);
    setValidationError("");

    // Use batch update for composite mode (when compositeIds provided), individual validation for regular mode
    const validationPromise = compositeIds
      ? API.batchUpdateInferences(sceneId, {
        ids: compositeIds,
        is_valid: isValid
      })
      : API.invalidateInference(sceneId, validationId)
        .then(() => API.validateInference(sceneId, validationId, isValid));

    return validationPromise
      .then(async () => {
        // Update the inference in the state
        const queryKey = ["scene-inferences", sceneId, JSON.stringify(filters)];
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(
          queryKey,
          (inferenceQueue: FeatureCollection<Geometry, InferenceProperties>) => {
            const features = inferenceQueue.features.map((inference) => {
              if (compositeIds) {
                // In composite mode, check if this composite's ids match the validation IDs
                if (!inference.properties.ids?.some(id => compositeIds.includes(id))) {
                  return inference;
                }
              } else {
                // In non-composite mode, check if this inference's id matches
                if (inference.properties.id !== validationId) return inference;
              }

              return {
                ...inference,
                properties: {
                  ...inference.properties,
                  is_valid: isValid,
                  validation_date: new Date().toISOString()
                }
              };
            });

            return {
              ...inferenceQueue,
              features
            };
          }
        );
      })
      .catch((e) => setValidationError(e.message))
      .finally(() => setIsValidating(false));
  };

  const updateInference = (options: {
    inferenceId: number;
    notes?: string;
    selected_class?: string | null;
    flagged?: boolean;
    compositeIds?: number[];
  }) => {
    const { inferenceId, notes, selected_class, flagged, compositeIds } = options;

    if (!sceneId) return;

    setIsUpdating(true);
    setUpdateError("");

    // Use batch update for composite mode (when compositeIds provided), individual update for regular mode
    const updatePromise = compositeIds
      ? API.batchUpdateInferences(sceneId, {
        ids: compositeIds,
        ...(notes !== undefined && { notes }),
        ...(selected_class !== undefined && { selected_class }),
        ...(flagged !== undefined && { flagged })
      })
      : API.updateInference(sceneId, inferenceId, notes, selected_class, flagged);

    return updatePromise
      .then(async () => {
        // Update the inference in the state
        const queryKey = ["scene-inferences", sceneId, JSON.stringify(filters)];
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(
          queryKey,
          (inferenceQueue: FeatureCollection<Geometry, InferenceProperties>) => {
            const features = inferenceQueue.features.map((inference) => {
              if (compositeIds) {
                // In composite mode, check if this composite's ids match the update IDs
                if (!inference.properties.ids?.some(id => compositeIds.includes(id))) {
                  return inference;
                }
              } else {
                // In non-composite mode, check if this inference's id matches
                if (inference.properties.id !== inferenceId) return inference;
              }

              return {
                ...inference,
                properties: {
                  ...inference.properties,
                  ...(notes !== undefined && { notes }),
                  ...(selected_class !== undefined && { selected_class: selected_class }),
                  ...(flagged !== undefined && { flagged })
                }
              };
            });

            return {
              ...inferenceQueue,
              features
            };
          }
        );
      })
      .catch((e) => setUpdateError(e.message))
      .finally(() => setIsUpdating(false));
  };


  const { isPending, error, data, refetch } = useQuery({
    queryKey: ["scene-inferences", sceneId, JSON.stringify(filters)],
    queryFn: () => {
      if (!sceneId) throw Error("Not found.");
      return API.getSceneInferences(sceneId, filters);
    },
    retry: false,
  });

  useEffect(() => {
    // Refresh the cached inferences list when the hook unmounts so users see the
    // correct number of not validated inferences when they enter the page next time.
    return () => {
      refetch();
    };
  }, [refetch]);

  return {
    isPending,
    error,
    inferences: data,
    refetch,
    validate,
    isValidating,
    validationError,
    updateInference,
    isUpdating,
    updateError
  };
}

export default useSceneInferences;
